import * as React from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  getCurrentLanguageFromUrl,
  getPathLanguage,
  isValidLanguageCode,
  updateUrlWithLanguage,
  type LanguageCode,
} from './url-language-utils'

export const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'zh', name: '简体中文', flag: '🇨🇳' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'tw', name: '繁體中文', flag: '🇭🇰' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
]

type TranslationValue = string | TranslationMap
type TranslationMap = { [key: string]: TranslationValue }

type TranslationModule = { default: TranslationMap } | TranslationMap

type I18nContextValue = {
  language: LanguageCode
  setLanguage: (code: string) => void
  t: (key: string, params?: Record<string, string | number>) => string
  isLoading: boolean
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined)

const translationModules = import.meta.glob('../languages/**/*.json') as Record<
  string,
  () => Promise<TranslationModule>
>

function getValueByPath(
  object: TranslationMap,
  path: string,
): string | undefined {
  const segments = path.split('.')
  let current: TranslationValue | undefined = object
  for (const segment of segments) {
    if (typeof current !== 'object' || current === null)
      return undefined
    current = (current as TranslationMap)[segment]
    if (current === undefined)
      return undefined
  }
  return typeof current === 'string' ? current : undefined
}

function deepMerge(
  target: TranslationMap,
  source: TranslationMap,
): TranslationMap {
  const result: TranslationMap = { ...target }
  for (const key of Object.keys(source)) {
    const targetValue = result[key]
    const sourceValue = source[key]
    if (sourceValue === undefined) {
      continue
    }
    result[key]
      = typeof targetValue === 'object'
        && targetValue !== null
        && typeof sourceValue === 'object'
        && sourceValue !== null
        ? deepMerge(
            targetValue as TranslationMap,
            sourceValue as TranslationMap,
          )
        : sourceValue
  }
  return result
}

async function loadLanguageTranslations(
  languageCode: string,
): Promise<TranslationMap> {
  let merged: TranslationMap = {}
  const loaders = Object.entries(translationModules)
    .filter(([path]) => path.includes(`/languages/${languageCode}/`))
    .map(([, loader]) => loader)

  for (const load of loaders) {
    const mod = await load()
    const data: TranslationMap
      = 'default' in mod
        ? (mod as { default: TranslationMap }).default
        : (mod as TranslationMap)
    merged = deepMerge(merged, data)
  }

  return merged
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>(
    getCurrentLanguageFromUrl,
  )
  const [messages, setMessages] = useState<TranslationMap>({})
  const [loadedLanguage, setLoadedLanguage] = useState<LanguageCode | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    loadLanguageTranslations(language)
      .then((data) => {
        if (cancelled)
          return
        setMessages(data)
        setLoadedLanguage(language)
      })
      .catch(() => {
        if (cancelled)
          return
        setMessages({})
        setLoadedLanguage(language)
      })
      .finally(() => {
        if (cancelled)
          return
        setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [language])

  useEffect(() => {
    const handlePopState = () => {
      const newLanguage = getCurrentLanguageFromUrl()
      if (newLanguage !== language) {
        setLanguageState(newLanguage)
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [language])

  const setLanguage = useCallback((code: string) => {
    const targetCode = isValidLanguageCode(code) ? code : 'en'

    try {
      localStorage.setItem('lang', targetCode)
    }
    catch {}

    updateUrlWithLanguage(targetCode, true)
    if (getPathLanguage(window.location.pathname) === targetCode)
      setLanguageState(targetCode)
  }, [])

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => {
      const value = getValueByPath(messages, key)
      if (value === undefined)
        return key
      if (!params)
        return value
      return value.replaceAll(/\{(\w+)\}/g, (_, k) =>
        params[k] !== undefined ? String(params[k]) : `{${k}}`)
    },
    [messages],
  )

  const translationsReady = !isLoading && loadedLanguage === language
  const value = useMemo<I18nContextValue>(
    () => ({ language, setLanguage, t, isLoading: !translationsReady }),
    [language, setLanguage, t, translationsReady],
  )

  return (
    <I18nContext.Provider value={value}>
      {translationsReady ? children : null}
    </I18nContext.Provider>
  )
}

export function useI18n() {
  const ctx = useContext(I18nContext)
  if (!ctx) {
    throw new Error('useI18n must be used within an I18nProvider')
  }
  return ctx
}
